import React, { useState, useEffect } from 'react';
import { 
  AiOutlinePlus, 
  AiOutlineSend, 
  AiOutlineEye, 
  AiOutlineUser, 
  AiOutlineTeam,
  AiOutlineShop,
  AiOutlineLoading3Quarters,
  AiOutlineDelete,
  AiOutlineEdit,
  AiOutlineSearch,
  AiOutlineFilter,
  AiOutlineCalendar,
  AiOutlineFileText,
  AiOutlineCheckCircle,
  AiOutlineClockCircle,
  AiOutlineExclamationCircle
} from 'react-icons/ai';
import { BsImage, BsThreeDotsVertical } from 'react-icons/bs';
import { FiUsers, FiBarChart2, FiRefreshCw, FiDownload } from 'react-icons/fi';
import { MdFilterList, MdClear } from 'react-icons/md';

function EnhancedNotification() {
  const [activeTab, setActiveTab] = useState('list');
  const [loading, setLoading] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [users, setUsers] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [fetchingLists, setFetchingLists] = useState(false);

  // Enhanced filtering state
  const [filters, setFilters] = useState({
    search: '',
    targetType: '',
    type: '',
    status: '',
    dateRange: {
      startDate: '',
      endDate: ''
    }
  });

  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    type: 'general',
    targetType: 'all_users',
    targetUsers: [],
    targetVendors: [],
    image: null
  });

  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingNotification, setEditingNotification] = useState(null);
  const [editFormData, setEditFormData] = useState({
    title: '',
    message: '',
    type: 'general'
  });
  const [editLoading, setEditLoading] = useState(false);

  // Enhanced pagination
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 12,
    totalPages: 1,
    totalNotifications: 0
  });

  // Statistics state
  const [stats, setStats] = useState({
    total: 0,
    sent: 0,
    pending: 0,
    failed: 0
  });

  // API Base URL
  const API_BASE = process.env.REACT_APP_API_URL || 'https://api.bookwie.com';

  const getAuthToken = () => {
    return localStorage.getItem('token');
  };

  const getHeaders = () => ({
    'Authorization': `Bearer ${getAuthToken()}`,
    'Content-Type': 'application/json'
  });

  const getFormDataHeaders = () => ({
    'Authorization': `Bearer ${getAuthToken()}`
  });

  // Fetch notifications with enhanced filtering
  const fetchNotifications = async (page = 1, resetPagination = false) => {
    try {
      setFetchingLists(true);
      
      // Build query parameters
      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: pagination.limit.toString(),
        sortBy,
        sortOrder
      });

      // Add filters to query
      if (filters.search) queryParams.append('search', filters.search);
      if (filters.targetType) queryParams.append('targetType', filters.targetType);
      if (filters.type) queryParams.append('type', filters.type);
      if (filters.status) queryParams.append('status', filters.status);
      if (filters.dateRange.startDate) queryParams.append('startDate', filters.dateRange.startDate);
      if (filters.dateRange.endDate) queryParams.append('endDate', filters.dateRange.endDate);

      const response = await fetch(
        `${API_BASE}/api/admin/notifications?${queryParams}`,
        {
          method: 'GET',
          headers: getHeaders()
        }
      );

      if (response.ok) {
        const data = await response.json();
        setNotifications(data.data.notifications);
        setPagination(prev => ({
          ...prev,
          page: data.data.pagination.currentPage,
          totalPages: data.data.pagination.totalPages,
          totalNotifications: data.data.pagination.totalNotifications
        }));

        // Update stats
        const notificationStats = data.data.notifications.reduce((acc, notif) => {
          acc.total++;
          if (notif.status === 'sent') acc.sent++;
          else if (notif.status === 'pending') acc.pending++;
          else if (notif.status === 'failed') acc.failed++;
          return acc;
        }, { total: 0, sent: 0, pending: 0, failed: 0 });
        
        setStats(notificationStats);
      } else {
        console.error('Failed to fetch notifications');
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setFetchingLists(false);
    }
  };

  // Fetch users for specific targeting
  const fetchUsers = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/user-listing?limit=100`, {
        method: 'GET',
        headers: getHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        setUsers(data.data.users || []);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  // Fetch vendors for specific targeting
  const fetchVendors = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/vendor/list?limit=100`, {
        method: 'GET',
        headers: getHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        setVendors(data.data.vendors || []);
      }
    } catch (error) {
      console.error('Error fetching vendors:', error);
    }
  };

  // Load data on component mount
  useEffect(() => {
    if (activeTab === 'list') {
      fetchNotifications();
    }
  }, [activeTab]);

  // Refetch when filters change
  useEffect(() => {
    if (activeTab === 'list') {
      fetchNotifications(1, true);
    }
  }, [filters, sortBy, sortOrder]);

  // Load users/vendors when specific targeting is selected
  useEffect(() => {
    if (formData.targetType === 'specific_users' && users.length === 0) {
      fetchUsers();
    }
    if (formData.targetType === 'specific_vendors' && vendors.length === 0) {
      fetchVendors();
    }
  }, [formData.targetType]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    setFormData(prev => ({
      ...prev,
      image: file
    }));
  };

  const handleTargetSelection = (e) => {
    const { value, checked } = e.target;
    const field = formData.targetType === 'specific_users' ? 'targetUsers' : 'targetVendors';
    
    setFormData(prev => ({
      ...prev,
      [field]: checked 
        ? [...prev[field], value]
        : prev[field].filter(id => id !== value)
    }));
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleDateRangeChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      dateRange: {
        ...prev.dateRange,
        [key]: value
      }
    }));
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      targetType: '',
      type: '',
      status: '',
      dateRange: {
        startDate: '',
        endDate: ''
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const submitData = new FormData();
      submitData.append('title', formData.title);
      submitData.append('message', formData.message);
      submitData.append('type', formData.type);
      submitData.append('targetType', formData.targetType);

      if (formData.targetType === 'specific_users' && formData.targetUsers.length > 0) {
        submitData.append('targetUsers', JSON.stringify(formData.targetUsers));
      }

      if (formData.targetType === 'specific_vendors' && formData.targetVendors.length > 0) {
        submitData.append('targetVendors', JSON.stringify(formData.targetVendors));
      }

      if (formData.image) {
        submitData.append('image', formData.image);
      }

      const response = await fetch(`${API_BASE}/api/admin/notification`, {
        method: 'POST',
        headers: getFormDataHeaders(),
        body: submitData
      });

      const result = await response.json();

      if (response.ok) {
        alert('Notification sent successfully!');
        
        setFormData({
          title: '',
          message: '',
          type: 'general',
          targetType: 'all_users',
          targetUsers: [],
          targetVendors: [],
          image: null
        });
        
        if (activeTab === 'list') {
          fetchNotifications();
        }
      } else {
        alert(`Error: ${result.message || 'Failed to send notification'}`);
      }
    } catch (error) {
      console.error('Error sending notification:', error);
      alert('Error sending notification. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const deleteNotification = async (notificationId) => {
    if (!window.confirm('Are you sure you want to delete this notification?')) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/api/admin/notification/${notificationId}`, {
        method: 'DELETE',
        headers: getHeaders()
      });

      if (response.ok) {
        alert('Notification deleted successfully!');
        fetchNotifications(pagination.page);
      } else {
        alert('Error deleting notification');
      }
    } catch (error) {
      console.error('Error deleting notification:', error);
      alert('Error deleting notification');
    }
  };

  // Open edit modal with notification data
  const openEditModal = (notification) => {
    setEditingNotification(notification);
    setEditFormData({
      title: notification.title || '',
      message: notification.message || '',
      type: notification.type || 'general'
    });
    setShowEditModal(true);
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editingNotification) return;

    setEditLoading(true);
    try {
      const submitData = new FormData();
      submitData.append('title', editFormData.title);
      submitData.append('message', editFormData.message);
      submitData.append('type', editFormData.type);

      const response = await fetch(
        `${API_BASE}/api/admin/notification/${editingNotification._id}`,
        {
          method: 'PATCH',
          headers: getFormDataHeaders(),
          body: submitData
        }
      );

      const result = await response.json();

      if (response.ok) {
        alert('Notification updated successfully!');
        setShowEditModal(false);
        setEditingNotification(null);
        fetchNotifications(pagination.page);
      } else {
        alert(`Error: ${result.message || 'Failed to update notification'}`);
      }
    } catch (error) {
      console.error('Error updating notification:', error);
      alert('Error updating notification. Please try again.');
    } finally {
      setEditLoading(false);
    }
  };

  const getNotificationStats = async (notificationId) => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/notification/${notificationId}/stats`, {
        method: 'GET',
        headers: getHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        alert(`Stats: ${data.data.totalRead}/${data.data.totalRecipients} read (${data.data.readPercentage}%)`);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const getTargetIcon = (targetType) => {
    switch (targetType) {
      case 'all_users': return <AiOutlineTeam className="w-5 h-5" />;
      case 'all_vendors': return <AiOutlineShop className="w-5 h-5" />;
      case 'specific_users': return <AiOutlineUser className="w-5 h-5" />;
      case 'specific_vendors': return <AiOutlineShop className="w-5 h-5" />;
      default: return <AiOutlineTeam className="w-5 h-5" />;
    }
  };

  const getTargetLabel = (targetType) => {
    switch (targetType) {
      case 'all_users': return 'All Users';
      case 'all_vendors': return 'All Vendors';
      case 'specific_users': return 'Specific Users';
      case 'specific_vendors': return 'Specific Vendors';
      default: return 'All Users';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'general': return '📢';
      case 'booking': return '📅';
      case 'payment': return '💳';
      case 'system': return '⚙️';
      case 'promotion': return '🎉';
      default: return '📢';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'sent': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'failed': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getReadPercentage = (totalRead, totalRecipients) => {
    if (totalRecipients === 0) return 0;
    return Math.round((totalRead / totalRecipients) * 100);
  };

  return (
    <div className="space-y-5">
      <div className="">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight text-gray-900">Notifications</h1>
          <p className="mt-2 text-gray-600">Send and manage notifications to users and vendors</p>
        </div>
        
        {/* Tab Navigation */}
        <div className="bg-white rounded-3xl shadow-card mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              <button
                onClick={() => setActiveTab('create')}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'create'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <AiOutlinePlus className="w-4 h-4 inline mr-2" />
                Create Notification
              </button>
              <button
                onClick={() => setActiveTab('list')}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'list'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <AiOutlineEye className="w-4 h-4 inline mr-2" />
                All Notifications ({pagination.totalNotifications})
              </button>
            </nav>
          </div>

          {/* Create Notification Tab */}
          {activeTab === 'create' && (
            <div className="p-6">
              <div className="mb-6">
                <h2 className="text-xl font-semibold text-gray-900">Create New Notification</h2>
                <p className="text-gray-600 mt-1">Fill in the details to send a notification</p>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Title */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Title <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      placeholder="Enter notification title"
                      required
                    />
                  </div>

                  {/* Type */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Notification Type
                    </label>
                    <select
                      name="type"
                      value={formData.type}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    >
                      <option value="general">📢 General</option>
                      <option value="booking">📅 Booking</option>
                      <option value="payment">💳 Payment</option>
                      <option value="system">⚙️ System</option>
                      <option value="promotion">🎉 Promotion</option>
                    </select>
                  </div>
                </div>

                {/* Target Type */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Send To <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    {['all_users', 'all_vendors', 'specific_users', 'specific_vendors'].map((option) => (
                      <label
                        key={option}
                        className={`relative flex items-center justify-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                          formData.targetType === option
                            ? 'border-blue-500 bg-blue-50 text-blue-700'
                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        <input
                          type="radio"
                          name="targetType"
                          value={option}
                          checked={formData.targetType === option}
                          onChange={handleInputChange}
                          className="sr-only"
                        />
                        <div className="flex flex-col items-center text-center">
                          {getTargetIcon(option)}
                          <span className="text-sm font-medium mt-2">{getTargetLabel(option)}</span>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Specific Users/Vendors Selection */}
                {(formData.targetType === 'specific_users' || formData.targetType === 'specific_vendors') && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-3">
                      Select {formData.targetType === 'specific_users' ? 'Users' : 'Vendors'}
                    </label>
                    <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-3">
                      {formData.targetType === 'specific_users' ? (
                        users.length > 0 ? users.map(user => (
                          <label key={user._id} className="flex items-center space-x-3 py-2">
                            <input
                              type="checkbox"
                              value={user._id}
                              checked={formData.targetUsers.includes(user._id)}
                              onChange={handleTargetSelection}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="text-sm">{user.name} ({user.phoneNumber})</span>
                          </label>
                        )) : <p className="text-gray-500">Loading users...</p>
                      ) : (
                        vendors.length > 0 ? vendors.map(vendor => (
                          <label key={vendor._id} className="flex items-center space-x-3 py-2">
                            <input
                              type="checkbox"
                              value={vendor._id}
                              checked={formData.targetVendors.includes(vendor._id)}
                              onChange={handleTargetSelection}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="text-sm">{vendor.store_name || vendor.name} ({vendor.phone})</span>
                          </label>
                        )) : <p className="text-gray-500">Loading vendors...</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Message */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Message <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows={5}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none"
                    placeholder="Enter your notification message here..."
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.message.length}/500 characters
                  </p>
                </div>

                {/* Image Upload */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Attach Image (Optional)
                  </label>
                  <div className="relative">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="hidden"
                      id="image-upload"
                    />
                    <label
                      htmlFor="image-upload"
                      className="flex items-center justify-center w-full p-6 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-gray-400 hover:bg-gray-50 transition-all"
                    >
                      <div className="text-center">
                        <BsImage className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                        <p className="text-sm text-gray-600">
                          Click to upload an image
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          PNG, JPG, GIF up to 10MB
                        </p>
                      </div>
                    </label>
                    {formData.image && (
                      <p className="text-sm text-green-600 mt-2">
                        ✓ {formData.image.name} selected
                      </p>
                    )}
                  </div>
                </div>

                {/* Submit Button */}
                <div className="flex justify-end pt-6 border-t border-gray-200">
                  <button
                    type="submit"
                    disabled={loading}
                    className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-medium rounded-lg hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    {loading ? (
                      <>
                        <AiOutlineLoading3Quarters className="animate-spin w-5 h-5 mr-2" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <AiOutlineSend className="w-5 h-5 mr-2" />
                        Send Notification
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Enhanced List Notifications Tab */}
          {activeTab === 'list' && (
            <div className="p-6">
              {/* Header with Stats and Actions */}
              <div className="mb-6">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">All Notifications</h2>
                    <p className="text-gray-600 mt-1">Manage and track your sent notifications</p>
                  </div>
                  
                  {/* Quick Stats */}
                  <div className="flex flex-wrap gap-4">
                    <div className="bg-blue-50 px-4 py-2 rounded-lg">
                      <div className="text-sm text-blue-600 font-medium">Total</div>
                      <div className="text-lg font-semibold text-blue-700">{stats.total}</div>
                    </div>
                    <div className="bg-green-50 px-4 py-2 rounded-lg">
                      <div className="text-sm text-green-600 font-medium">Sent</div>
                      <div className="text-lg font-semibold text-green-700">{stats.sent}</div>
                    </div>
                    <div className="bg-yellow-50 px-4 py-2 rounded-lg">
                      <div className="text-sm text-yellow-600 font-medium">Pending</div>
                      <div className="text-lg font-semibold text-yellow-700">{stats.pending}</div>
                    </div>
                    <div className="bg-red-50 px-4 py-2 rounded-lg">
                      <div className="text-sm text-red-600 font-medium">Failed</div>
                      <div className="text-lg font-semibold text-red-700">{stats.failed}</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Search and Filter Section */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                  {/* Search Bar */}
                  <div className="flex-1">
                    <div className="relative">
                      <AiOutlineSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <input
                        type="text"
                        placeholder="Search notifications by title or message..."
                        value={filters.search}
                        onChange={(e) => handleFilterChange('search', e.target.value)}
                        className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  {/* Filter Toggle and Refresh */}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setShowFilters(!showFilters)}
                      className={`inline-flex items-center px-4 py-2.5 border rounded-lg font-medium transition-colors ${
                        showFilters 
                          ? 'border-blue-500 bg-blue-50 text-blue-700' 
                          : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <MdFilterList className="w-4 h-4 mr-2" />
                      Filters
                    </button>
                    
                    <button
                      onClick={() => fetchNotifications(pagination.page)}
                      className="inline-flex items-center px-4 py-2.5 border border-gray-300 bg-white text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors"
                    >
                      <FiRefreshCw className="w-4 h-4 mr-2" />
                      Refresh
                    </button>
                  </div>
                </div>

                {/* Advanced Filters */}
                {showFilters && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      {/* Target Type Filter */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Target Type</label>
                        <select
                          value={filters.targetType}
                          onChange={(e) => handleFilterChange('targetType', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">All Targets</option>
                          <option value="all_users">All Users</option>
                          <option value="all_vendors">All Vendors</option>
                          <option value="specific_users">Specific Users</option>
                          <option value="specific_vendors">Specific Vendors</option>
                        </select>
                      </div>

                      {/* Notification Type Filter */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
                        <select
                          value={filters.type}
                          onChange={(e) => handleFilterChange('type', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">All Types</option>
                          <option value="general">General</option>
                          <option value="booking">Booking</option>
                          <option value="payment">Payment</option>
                          <option value="system">System</option>
                          <option value="promotion">Promotion</option>
                        </select>
                      </div>

                      {/* Status Filter */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                        <select
                          value={filters.status}
                          onChange={(e) => handleFilterChange('status', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">All Status</option>
                          <option value="sent">Sent</option>
                          <option value="pending">Pending</option>
                          <option value="failed">Failed</option>
                        </select>
                      </div>

                      {/* Sort Options */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Sort By</label>
                        <select
                          value={`${sortBy}-${sortOrder}`}
                          onChange={(e) => {
                            const [field, order] = e.target.value.split('-');
                            setSortBy(field);
                            setSortOrder(order);
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="createdAt-desc">Newest First</option>
                          <option value="createdAt-asc">Oldest First</option>
                          <option value="totalRecipients-desc">Most Recipients</option>
                          <option value="totalRead-desc">Most Read</option>
                          <option value="title-asc">Title A-Z</option>
                        </select>
                      </div>
                    </div>

                    {/* Date Range Filter */}
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Date Range</label>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <input
                            type="date"
                            value={filters.dateRange.startDate}
                            onChange={(e) => handleDateRangeChange('startDate', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Start Date"
                          />
                        </div>
                        <div>
                          <input
                            type="date"
                            value={filters.dateRange.endDate}
                            onChange={(e) => handleDateRangeChange('endDate', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="End Date"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Clear Filters */}
                    <div className="mt-4 flex justify-end">
                      <button
                        onClick={clearFilters}
                        className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        <MdClear className="w-4 h-4 mr-2" />
                        Clear Filters
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Notifications List */}
              {fetchingLists ? (
                <div className="text-center py-12">
                  <AiOutlineLoading3Quarters className="animate-spin w-8 h-8 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">Loading notifications...</p>
                </div>
              ) : notifications.length > 0 ? (
                <div className="space-y-4">
                  {notifications.map((notification) => (
                    <div key={notification._id} className="bg-white rounded-lg border border-gray-200 hover:shadow-md transition-shadow">
                      <div className="p-6">
                        <div className="flex items-start justify-between">
                          {/* Main Content */}
                          <div className="flex-1">
                            <div className="flex items-start space-x-4">
                              {/* Notification Image */}
                              {notification.image && (
                                <div className="flex-shrink-0">
                                  <img
                                    src={notification.image}
                                    alt="Notification"
                                    className="w-16 h-16 rounded-lg object-cover"
                                  />
                                </div>
                              )}
                              
                              {/* Content */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center space-x-3 mb-2">
                                  <h3 className="text-lg font-semibold text-gray-900 truncate">
                                    {notification.title}
                                  </h3>
                                  
                                  {/* Type Badge */}
                                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                    notification.type === 'general' ? 'bg-blue-100 text-blue-800' :
                                    notification.type === 'booking' ? 'bg-green-100 text-green-800' :
                                    notification.type === 'payment' ? 'bg-yellow-100 text-yellow-800' :
                                    notification.type === 'system' ? 'bg-red-100 text-red-800' :
                                    'bg-purple-100 text-purple-800'
                                  }`}>
                                    {getTypeIcon(notification.type)} {notification.type}
                                  </span>
                                  
                                  {/* Status Badge */}
                                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(notification.status)}`}>
                                    {notification.status === 'sent' && <AiOutlineCheckCircle className="w-3 h-3 mr-1" />}
                                    {notification.status === 'pending' && <AiOutlineClockCircle className="w-3 h-3 mr-1" />}
                                    {notification.status === 'failed' && <AiOutlineExclamationCircle className="w-3 h-3 mr-1" />}
                                    {notification.status}
                                  </span>
                                </div>
                                
                                <p className="text-gray-600 mb-4 line-clamp-2">{notification.message}</p>
                                
                                {/* Stats Row */}
                                <div className="flex flex-wrap items-center gap-6 text-sm text-gray-500">
                                  <span className="flex items-center">
                                    {getTargetIcon(notification.targetType)}
                                    <span className="ml-1">{getTargetLabel(notification.targetType)}</span>
                                  </span>
                                  
                                  <span className="flex items-center">
                                    <FiUsers className="w-4 h-4 mr-1" />
                                    {notification.totalRecipients} recipients
                                  </span>
                                  
                                  <span className="flex items-center">
                                    <AiOutlineEye className="w-4 h-4 mr-1" />
                                    {notification.totalRead} read ({getReadPercentage(notification.totalRead, notification.totalRecipients)}%)
                                  </span>
                                  
                                  <span className="flex items-center">
                                    <AiOutlineCalendar className="w-4 h-4 mr-1" />
                                    {formatDate(notification.createdAt)}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Actions */}
                          <div className="flex items-center space-x-2 ml-4">
                            {/* Read Percentage Progress */}
                            <div className="hidden lg:block mr-4">
                              <div className="text-xs text-gray-500 mb-1">Read Rate</div>
                              <div className="w-20 h-2 bg-gray-200 rounded-full overflow-hidden">
                                <div 
                                  className="h-full bg-blue-500 transition-all duration-300"
                                  style={{ width: `${getReadPercentage(notification.totalRead, notification.totalRecipients)}%` }}
                                />
                              </div>
                              <div className="text-xs text-gray-600 mt-1">
                                {getReadPercentage(notification.totalRead, notification.totalRecipients)}%
                              </div>
                            </div>

                            {/* Action Buttons */}
                            <button
                              onClick={() => getNotificationStats(notification._id)}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title="View Detailed Stats"
                            >
                              <FiBarChart2 className="w-4 h-4" />
                            </button>

                            <button
                              onClick={() => openEditModal(notification)}
                              className="p-2 text-yellow-600 hover:bg-yellow-50 rounded-lg transition-colors"
                              title="Edit Notification"
                            >
                              <AiOutlineEdit className="w-4 h-4" />
                            </button>

                            <button
                              onClick={() => deleteNotification(notification._id)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Delete Notification"
                            >
                              <AiOutlineDelete className="w-4 h-4" />
                            </button>

                            {/* More Options */}
                            <div className="relative">
                              <button className="p-2 text-gray-600 hover:bg-gray-50 rounded-lg transition-colors">
                                <BsThreeDotsVertical className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {/* Enhanced Pagination */}
                  {pagination.totalPages > 1 && (
                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                        <div className="text-sm text-gray-700 mb-4 sm:mb-0">
                          Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.totalNotifications)} of {pagination.totalNotifications} notifications
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          {/* Previous Button */}
                          <button
                            onClick={() => fetchNotifications(pagination.page - 1)}
                            disabled={pagination.page === 1}
                            className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Previous
                          </button>

                          {/* Page Numbers */}
                          <div className="flex space-x-1">
                            {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                              let pageNum;
                              if (pagination.totalPages <= 5) {
                                pageNum = i + 1;
                              } else if (pagination.page <= 3) {
                                pageNum = i + 1;
                              } else if (pagination.page >= pagination.totalPages - 2) {
                                pageNum = pagination.totalPages - 4 + i;
                              } else {
                                pageNum = pagination.page - 2 + i;
                              }

                              return (
                                <button
                                  key={pageNum}
                                  onClick={() => fetchNotifications(pageNum)}
                                  className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                                    pagination.page === pageNum
                                      ? 'bg-blue-600 text-white'
                                      : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                                  }`}
                                >
                                  {pageNum}
                                </button>
                              );
                            })}
                          </div>

                          {/* Next Button */}
                          <button
                            onClick={() => fetchNotifications(pagination.page + 1)}
                            disabled={pagination.page === pagination.totalPages}
                            className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Next
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-16">
                  <div className="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                    <AiOutlineFileText className="w-12 h-12 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Notifications Found</h3>
                  <p className="text-gray-600 mb-6">
                    {Object.values(filters).some(filter => 
                      filter && (typeof filter === 'string' ? filter.length > 0 : Object.values(filter).some(v => v))
                    ) 
                      ? 'No notifications match your current filters. Try adjusting your search criteria.'
                      : 'Create your first notification to get started communicating with your users and vendors.'
                    }
                  </p>
                  {Object.values(filters).some(filter => 
                    filter && (typeof filter === 'string' ? filter.length > 0 : Object.values(filter).some(v => v))
                  ) && (
                    <button
                      onClick={clearFilters}
                      className="inline-flex items-center px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <MdClear className="w-4 h-4 mr-2" />
                      Clear Filters
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Edit Notification Modal */}
        {showEditModal && editingNotification && (
          <div className="fixed inset-0 bg-half-transparent flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-3xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex justify-between items-center">
                <h3 className="text-xl font-bold text-gray-900">Edit Notification</h3>
                <button
                  onClick={() => { setShowEditModal(false); setEditingNotification(null); }}
                  className="text-gray-500 hover:text-gray-700 p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <MdClear className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleEditSubmit} className="p-6 space-y-6">
                {/* Title */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Title <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={editFormData.title}
                    onChange={handleEditInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    placeholder="Enter notification title"
                    required
                  />
                </div>

                {/* Type */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Notification Type
                  </label>
                  <select
                    name="type"
                    value={editFormData.type}
                    onChange={handleEditInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  >
                    <option value="general">General</option>
                    <option value="booking">Booking</option>
                    <option value="payment">Payment</option>
                    <option value="system">System</option>
                    <option value="promotion">Promotion</option>
                  </select>
                </div>

                {/* Message */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Message <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="message"
                    value={editFormData.message}
                    onChange={handleEditInputChange}
                    rows={5}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none"
                    placeholder="Enter your notification message here..."
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {editFormData.message.length}/500 characters
                  </p>
                </div>

                {/* Info about original notification */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Original Notification Info</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
                    <div>
                      <span className="font-medium">Target:</span> {getTargetLabel(editingNotification.targetType)}
                    </div>
                    <div>
                      <span className="font-medium">Status:</span>{' '}
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(editingNotification.status)}`}>
                        {editingNotification.status}
                      </span>
                    </div>
                    <div>
                      <span className="font-medium">Recipients:</span> {editingNotification.totalRecipients}
                    </div>
                    <div>
                      <span className="font-medium">Created:</span> {formatDate(editingNotification.createdAt)}
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => { setShowEditModal(false); setEditingNotification(null); }}
                    className="px-6 py-2.5 bg-gray-500 text-white font-medium rounded-lg hover:bg-gray-600 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={editLoading}
                    className="inline-flex items-center px-6 py-2.5 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    {editLoading ? (
                      <>
                        <AiOutlineLoading3Quarters className="animate-spin w-4 h-4 mr-2" />
                        Updating...
                      </>
                    ) : (
                      <>
                        <AiOutlineEdit className="w-4 h-4 mr-2" />
                        Update Notification
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default EnhancedNotification;